// File: src/blueprint/components/renderers/ContentRenderer.jsx
// GOAL:
// - Single source of truth for rendering step content.
// - For info steps: prefer primary `blocks` when present, otherwise fall back to InfoContent.
// - For input steps: render support/context blocks first, then the question UI.
// - For report steps: render report entries belonging to the requested reportId.

import ContextBlocks from '../content/ContextBlocks.jsx';
import InfoContent from '../content/InfoContent.jsx';
import QuestionContent from '../content/QuestionContent.jsx';
import BlockRenderer from './BlockRenderer.jsx';

const QUESTION_TYPES = new Set([
  'single',
  'multi',
  'text',
  'textarea',
  'rank',
  'matrix_single',
  'select_cards',
]);

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function asString(value) {
  return typeof value === 'string' ? value : '';
}

function normalizeText(value) {
  return asString(value).trim();
}

export function getResolvedStepType(item) {
  const kind = normalizeText(item?.kind);
  const inputType = normalizeText(item?.inputType);
  const legacyType = normalizeText(item?.type);

  if (kind === 'info') return 'info';
  if (kind === 'input') return inputType;
  if (kind === 'report') return 'report';

  return inputType || legacyType || kind;
}

function renderPrimaryInfoBlocks(item) {
  const blocks = asArray(item?.blocks);

  if (!blocks.length) {
    return null;
  }

  return (
    <div className="dv-br-info-blocks">
      {blocks.map((block, index) => (
        <BlockRenderer
          key={block?.id || `${String(item?.id || 'info')}:block:${index}`}
          block={block}
        />
      ))}
    </div>
  );
}

function getReportEntriesMap(reportEntriesById) {
  return asObject(reportEntriesById);
}

function readReportTargetFromActions(item) {
  const actions = asArray(item?.actions);

  for (const action of actions) {
    if (!action || typeof action !== 'object') {
      continue;
    }

    const type = normalizeText(action?.type);
    const targetId = normalizeText(action?.targetId);
    const reportId = normalizeText(action?.reportId);

    if (type === 'open_report') {
      if (reportId) return reportId;
      if (targetId) return targetId;
    }
  }

  return '';
}

function findReportEntriesByStepId(stepId, reportEntriesById) {
  if (!stepId) {
    return [];
  }

  const map = getReportEntriesMap(reportEntriesById);

  for (const entries of Object.values(map)) {
    const safeEntries = asArray(entries);
    const match = safeEntries.some((entry) => {
      const sourceId = normalizeText(entry?.sourceId);
      const reportId = normalizeText(entry?.reportId || entry?.raw?.reportId);
      const placementGroupId = normalizeText(entry?.placement?.groupId);

      return sourceId === stepId || reportId === stepId || placementGroupId === stepId;
    });

    if (match) {
      return safeEntries;
    }
  }

  return [];
}

function getReportEntriesForStep(item, reportEntriesById, reportEntries = []) {
  const directEntries = asArray(reportEntries);
  if (directEntries.length) {
    return directEntries;
  }

  const map = getReportEntriesMap(reportEntriesById);

  const directReportId = normalizeText(item?.reportId);
  if (directReportId && Array.isArray(map[directReportId])) {
    return map[directReportId];
  }

  const actionReportId = readReportTargetFromActions(item);
  if (actionReportId && Array.isArray(map[actionReportId])) {
    return map[actionReportId];
  }

  const stepId = normalizeText(item?.id);
  return findReportEntriesByStepId(stepId, map);
}

function getResolvedReportId(item, entries) {
  const directReportId = normalizeText(item?.reportId);
  if (directReportId) {
    return directReportId;
  }

  const actionReportId = readReportTargetFromActions(item);
  if (actionReportId) {
    return actionReportId;
  }

  const firstEntry = asArray(entries)[0] || null;
  const entryReportId = normalizeText(firstEntry?.reportId || firstEntry?.raw?.reportId);
  if (entryReportId) {
    return entryReportId;
  }

  return normalizeText(item?.id);
}

function getReportStepTitle(item, entries) {
  const directTitle = normalizeText(item?.title);
  if (directTitle) {
    return directTitle;
  }

  const firstEntry = asArray(entries)[0] || null;
  const reportTitle = normalizeText(firstEntry?.reportTitle || firstEntry?.raw?.reportTitle);
  if (reportTitle) {
    return reportTitle;
  }

  const entryTitle = normalizeText(firstEntry?.title);
  if (entryTitle) {
    return entryTitle;
  }

  return 'Report';
}

function AnswerReviewContent({ entry }) {
  const payload = asObject(entry?.payload);
  const items = asArray(payload?.items);

  if (!items.length) {
    return <p className="dv-br-card__text dv-br-card__muted">This review is not available yet.</p>;
  }

  return (
    <div className="dv-br-answerReview">
      {items.map((item, index) => {
        const questionId = normalizeText(item?.questionId) || `question-${index}`;
        const questionText = normalizeText(item?.questionText);
        const inputType = normalizeText(item?.inputType);
        const options = asArray(item?.options);
        const selectedValue = normalizeText(item?.selectedValue);
        const selectedValues = asArray(item?.selectedValues).map((value) => normalizeText(value));
        const minSelections = Number(item?.minSelections ?? 0);
        const maxSelections = Number(item?.maxSelections ?? 0);

        return (
          <section
            key={questionId}
            className="dv-br-answerReview__item"
            data-question-id={questionId}
          >
            <h3 className="dv-br-answerReview__question">{questionText || questionId}</h3>
            {(inputType === 'multi' || inputType === 'rank') &&
            (minSelections > 0 || maxSelections > 0) ? (
              <p className="dv-br-answerReview__meta">
                Select {minSelections || 0}
                {maxSelections > 0 ? `, to ${maxSelections} options` : ''}
              </p>
            ) : null}

            {options.length ? (
              <div className="dv-br-answerReview__options">
                {options.map((option, optionIndex) => {
                  console.log('report option', option);
                  const optionValue = normalizeText(option?.value);
                  const optionLabel = normalizeText(option?.label) || optionValue;
                  const isSelected =
                    inputType === 'multi'
                      ? selectedValues.includes(optionValue)
                      : selectedValue === optionValue;

                  return (
                    <div
                      key={`${questionId}:option:${optionValue || optionIndex}`}
                      className={`dv-br-answerReview__option ${isSelected ? 'is-selected' : ''}`}
                    >
                      <input type="checkbox" checked={isSelected} readOnly tabIndex={-1} />
                      <span className="dv-br-answerReview__optionLabel">
                        {isSelected ? <strong>{optionLabel}</strong> : optionLabel}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

function ReportEntry({ entry, entryIndex }) {
  const entryTitle = normalizeText(entry?.title);
  const blocks = asArray(entry?.blocks);
  const reportType = normalizeText(entry?.reportType || entry?.raw?.reportType);
  const reportId = normalizeText(entry?.reportId || entry?.raw?.reportId);

  const shouldHideTitle = reportType === 'answer_review' || reportId === 'answer_review';

  if (!entryTitle && !blocks.length && !entry?.payload) {
    return null;
  }

  if (reportType === 'answer_review' || reportId === 'answer_review') {
    return (
      <section
        className="dv-br-report-entry"
        data-entry-id={entry?.id || `report-entry-${entryIndex}`}
        data-report-id={entry?.reportId || entry?.raw?.reportId || ''}
      >
        <AnswerReviewContent entry={entry} />
      </section>
    );
  }

  return (
    <section
      className="dv-br-report-entry"
      data-entry-id={entry?.id || `report-entry-${entryIndex}`}
      data-report-id={entry?.reportId || entry?.raw?.reportId || ''}
    >
      {!shouldHideTitle && entryTitle ? (
        <h3 className="dv-br-report-entry__title">{entryTitle}</h3>
      ) : null}

      {blocks.length ? (
        <div className="dv-br-report-entry__blocks">
          {blocks.map((block, blockIndex) => (
            <BlockRenderer
              key={block?.id || `${entry?.id || `report-entry-${entryIndex}`}:block:${blockIndex}`}
              block={block}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function ReportContent({
  item,
  reportEntriesById,
  reportEntries = [],
  reportSubjectName = '',
  pilotIdentity = null,
}) {
  const entries = getReportEntriesForStep(item, reportEntriesById, reportEntries);
  const reportId = getResolvedReportId(item, entries);
  const title = getReportStepTitle(item, entries);
  const introBlocks = asArray(item?.blocks);

  console.log('reportSubjectName', reportSubjectName);
  console.log('pilotIdentity', pilotIdentity);
  const handlePrint = () => {
    const previousTitle = document.title;
    const printTitle = makePdfFilenameTitle(
      reportSubjectName + '_' + title,
      reportId || 'Savora_Report'
    );

    document.title = printTitle || 'Savora_Report';

    window.print();

    setTimeout(() => {
      document.title = previousTitle;
    }, 500);
  };

  return (
    <div className="dv-br-report-step" data-report-id={reportId || ''}>
      <div className="dv-br-report-step__header">
        <div>
          <h3>{reportSubjectName}</h3>
          <h2 className="dv-br-report-step__title">{title}</h2>
        </div>

        <button type="button" className="dv-br-report-step__print-button" onClick={handlePrint}>
          Print / Save as PDF
        </button>
      </div>

      <div className="dv-br-report-step__identity">
        <strong>Private pilot report for</strong>
        <div>
          User #{pilotIdentity?.wpUserId ?? null} - {pilotIdentity?.email ?? null} (
          {pilotIdentity?.generatedAt ?? null})
        </div>
        <div>&nbsp;</div>
      </div>

      {introBlocks.length ? (
        <div className="dv-br-report-step__intro">
          {introBlocks.map((block, index) => (
            <BlockRenderer
              key={block?.id || `${String(item?.id || 'report')}:intro:${index}`}
              block={block}
            />
          ))}
        </div>
      ) : null}

      {entries.length ? (
        <div className="dv-br-report-step__entries">
          {entries.map((entry, index) => (
            <ReportEntry
              key={entry?.id || `${reportId || 'report'}:entry:${index}`}
              entry={entry}
              entryIndex={index}
            />
          ))}
        </div>
      ) : (
        <p className="dv-br-card__text dv-br-card__muted">This report is not available yet.</p>
      )}
    </div>
  );
}

function makePdfFilenameTitle(title, fallback = 'Savora_Report') {
  return String(title || fallback)
    .replace(/^[^\wA-Za-z0-9]+/u, '') // leading icon/emoji weg
    .replace(/[^\w\s-]/g, '') // rare tekens weg
    .trim()
    .replace(/\s+/g, '_'); // spaties naar _
}

export default function ContentRenderer({
  item,
  packId,
  reportEntriesById = {},
  reportEntries = [],
  reportSubjectName = '',
  pilotIdentity = null,
}) {
  if (!item) {
    return (
      <div>
        <p className="dv-br-card__text dv-br-card__muted">No content.</p>
      </div>
    );
  }

  const stepId = String(item?.id ?? '');
  const resolvedType = getResolvedStepType(item);
  const hasPrimaryBlocks = asArray(item?.blocks).length > 0;

  if (resolvedType === 'report') {
    return (
      <div className="dv-br-report-shell" onContextMenu={(event) => event.preventDefault()}>
        {pilotIdentity?.wpUserId && (
          <div className="dv-br-watermark" aria-hidden="true">
            Report for #{pilotIdentity?.wpUserId} {pilotIdentity?.email}
          </div>
        )}
        <ReportContent
          item={item}
          reportEntriesById={reportEntriesById}
          reportEntries={reportEntries}
          reportSubjectName={reportSubjectName}
          pilotIdentity={pilotIdentity}
        />
        <ContextBlocks key={`ctx-step-${stepId}`} item={item} packId={packId} />
      </div>
    );
  }

  if (resolvedType === 'info' || resolvedType === 'video') {
    return (
      <>
        {hasPrimaryBlocks ? (
          <div key={`info-blocks-step-${stepId}`} className="dv-br-info-step">
            {renderPrimaryInfoBlocks(item)}
          </div>
        ) : (
          <InfoContent key={`info-step-${stepId}`} item={item} packId={packId} />
        )}

        <ContextBlocks key={`ctx-step-${stepId}`} item={item} packId={packId} />
      </>
    );
  }

  if (QUESTION_TYPES.has(resolvedType)) {
    return (
      <>
        <ContextBlocks key={`ctx-step-${stepId}`} item={item} packId={packId} />
        <QuestionContent key={`question-step-${stepId}`} item={item} packId={packId} />
      </>
    );
  }

  return (
    <div>
      <ContextBlocks key={`ctx-step-${stepId}`} item={item} packId={packId} />
      <p className="dv-br-card__text dv-br-card__muted">
        Unsupported step type: <strong>{resolvedType || 'unknown'}</strong>
      </p>
    </div>
  );
}
